import {
	type ConnectionProperties,
	type CustomerModelDTO,
	type MarketViewAssetClassAlias,
	type MarketViewSettings,
	BondDataAccessTier,
	BucketControllerApiFactory,
	ExportDtoExportConnectionTypeEnum,
	FundDataAccessTier,
	IntegrationEventEventEnum,
	IntegrationsControllerV3ApiFactory,
	ProtocolsControllerApiFactory,
	SnapshotRuleTypeEnum,
} from "@eu.mdotm.api/sphere";
import { useApiGen } from "$root/api/hooks";
import { DropzoneArea } from "$root/components/DropzoneArea";
import { IconWalls, WallOverlay } from "$root/components/IconWall";
import { typedUrlForRoute } from "$root/components/PlatformRouter/RoutesDef";
import type { MinimumDialogProps } from "$root/components/spawnable/type";
import UploadButton from "$root/components/UploadButton";
import EmptyImportBox from "$root/functional-areas/instruments-editor/spawn/box/EmptyImportBox";
import SuccessImportBox from "$root/functional-areas/instruments-editor/spawn/box/SuccessImportBox";
import { isExecutionWebhookActive } from "$root/functional-areas/portfolio/execution";
import { userRepository } from "$root/functional-areas/user/repository";
import { platformToast } from "$root/notification-system/toast";
import { axiosExtract, backendErrorMessage } from "$root/third-party-integrations/axios";
import { useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import {
	CUSTOMER_DECIMAL_PLACES_MAX,
	CUSTOMER_DECIMAL_PLACES_MIN,
	defaultNewCustomerData,
	type IntegrationApiKeyStatus,
	makeCustomerSchema,
	missingSmartXSettings,
	type SmartXRequiredField,
	SNAPSHOT_RULE_DAYS_MAX,
	SNAPSHOT_RULE_DAYS_MIN,
	snapshotPolicyFormValues,
	snapshotPolicyToSave,
} from "./customer-schema";
import { type SecretStatus, SecretField } from "./SecretField";
import { FormFields } from "$root/ui-lib/form/FormFields";
import { LocalizedError } from "$root/utils/errors";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { Button, IconButton, FormField, Label, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import {
	type CollapsibleHeaderProps,
	ActionText,
	Banner,
	Collapsible,
	Column,
	DefaultCollapsibleContent,
	DefaultCollapsibleHeader,
	DefaultTabTitle,
	Dialog,
	DialogFooter,
	Icon,
	Row,
	Tab,
	TabGroup,
	Text,
} from "@mdotm/shadcn/legacy/components";
import {
	type SpawnResult,
	adaptAnimatedNodeProvider,
	spawn,
	Switch,
	toClassName,
} from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach } from "@mdotm/utils/react-extra";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { clamp, unpromisify } from "@mdotm/shadcn/legacy/utils";
import React, { type ReactNode, useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

type SpawnUpdateCustomerDialogProps = {
	customerData: CustomerModelDTO;
};
export function spawnCustomerDialog(props?: SpawnUpdateCustomerDialogProps): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ show, resolve, onHidden }) => (
			<CustomerBox
				{...props}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
				onSubmitAsync={async () => {
					resolve();
				}}
			/>
		)),
	);
}

const integrationEventKeys = Object.values(IntegrationEventEventEnum);
const integrationEventLabels: Record<IntegrationEventEventEnum, string> = {
	EXECUTE: "Execute",
	PROPOSAL_ACCEPTED: "Proposal accepted",
	PROPOSAL_READY: "Proposal ready",
	AS_OF_COMMENTARY_READY: "As of commentary ready",
	AS_OF_METRICS_READY: "As of metrics ready",
	MANUAL_ACTION: "Manual action",
	REPORTS_READY: "Reports ready",
};

// Bond data access tier — the "data download license level" the customer is licensed for.
const bondDataAccessTierLabels: Record<BondDataAccessTier, string> = {
	PREMIUM: "Premium",
	BASE: "Base",
};
const bondDataAccessTierOptions = Object.values(BondDataAccessTier).map((tier) => ({
	label: bondDataAccessTierLabels[tier],
	value: tier,
}));

// Fund data access tier — the funds premium-data (Lipper) license level, independent of the bond tier above.
const fundDataAccessTierLabels: Record<FundDataAccessTier, string> = {
	PREMIUM: "Premium",
	BASE: "Base",
};
const fundDataAccessTierOptions = Object.values(FundDataAccessTier).map((tier) => ({
	label: fundDataAccessTierLabels[tier],
	value: tier,
}));

const exportConnectionTypeLabels: Record<ExportDtoExportConnectionTypeEnum, string> = {
	SFTP: "SFTP",
	MULTI_FILES_SFTP: "Multi files SFTP",
	FILE_CLOUD: "File cloud",
};
const exportConnectionTypeOptions = Object.values(ExportDtoExportConnectionTypeEnum).map((type) => ({
	label: exportConnectionTypeLabels[type],
	value: type,
}));

const snapshotRuleTypeLabels: Record<SnapshotRuleTypeEnum, string> = {
	LAST_DAYS_OF_MONTH: "Last days of month",
};
const snapshotRuleTypeOptions = Object.values(SnapshotRuleTypeEnum).map((type) => ({
	label: snapshotRuleTypeLabels[type],
	value: type,
}));

type SmartXSecret = "clientSecret" | "password";

const integrationApiKeySecretStatus: Record<IntegrationApiKeyStatus, SecretStatus> = {
	checking: "checking",
	stored: "set",
	absent: "unset",
	unknown: "unknown",
};

/** The field the backend names in a save it refuses on the webhook API key. */
const INTEGRATION_API_KEY_FIELD = "integration.apiKey";

/** The backend's message when it refused the save on the webhook API key, or undefined for any other failure. */
async function integrationApiKeyRejection(error: unknown): Promise<string | undefined> {
	const params = isAxiosError<{ params?: unknown }>(error) ? error.response?.data?.params : undefined;
	if (!Array.isArray(params) || !params.includes(INTEGRATION_API_KEY_FIELD)) return undefined;
	return backendErrorMessage(error);
}

const LoadCustomImage = (props: { onChange?(file?: string): void }) => {
	const ACCEPTED_FILE = "image/*";
	const { t } = useTranslation();
	const bucketApi = useApiGen(BucketControllerApiFactory);

	const [isVerifying, setIsVerifying] = useState(false);

	const { watch, reset, setValue } = useForm<{
		filename: string;
		fileContent: null | File;
	}>({
		defaultValues: {
			filename: "",
			fileContent: null satisfies null | File,
		},
	});

	const observableFileName = watch("filename");
	const observableFileContentController = watch("fileContent");

	const handleFileChange = React.useCallback(
		async (file: File | null) => {
			try {
				setIsVerifying(true);
				if (!file) {
					props.onChange?.(undefined);
					return reset();
				}

				const fileUrl = await axiosExtract(bucketApi.uploadFile(file));
				setValue("fileContent", file);
				setValue("filename", file.name);
				props.onChange?.(fileUrl);
			} catch (err) {
				console.log(err);
				if (err instanceof LocalizedError) {
					platformToast({
						children: err.endUserMsg,
						severity: "error",
						icon: "Positioning-indicator",
					});
				} else {
					platformToast({
						children: t("PORTFOLIOS.PORTFOLIO_GENERIC_UPLOAD_ERROR"),
						severity: "error",
						icon: "Positioning-indicator",
					});
				}
			} finally {
				setIsVerifying(false);
			}
		},
		[bucketApi, props, reset, setValue, t],
	);

	const uploadState = useMemo(() => {
		if (observableFileName) {
			return "success";
		}

		return "empty";
	}, [observableFileName]);

	const BoxMap = {
		empty: <EmptyImportBox />,
		success: <SuccessImportBox />,
	} satisfies Record<typeof uploadState, ReactNode>;

	return (
		<DropzoneArea
			onChange={unpromisify(handleFileChange)}
			accept={ACCEPTED_FILE}
			disabled={isVerifying}
			childrenWrapperAppearance={{
				className: {
					[`relative rounded-sm flex flex-1 gap-4 p-2 justify-between items-center border-2 `]: true,
					[`bg-[color:var(--palette-N50)] border-dashed border-[color:var(--palette-N500)]`]:
						observableFileName === null,
					[`bg-[color:var(--palette-N0)] border-[color:var(--palette-P600)]`]: Boolean(observableFileName),
				},
			}}
		>
			<div className="flex gap-2 items-center">
				{BoxMap[uploadState]}
				<p className="font-semibold">{observableFileName || "Select a file or drag and drop here"}</p>
			</div>

			{observableFileContentController ? (
				<IconButton
					unstyled
					onClick={() => reset()}
					data-qualifier="userSetting/Dropzone/delete"
					icon="Delete"
					size={20}
					color={themeCSSVars.palette_N500}
				/>
			) : (
				<UploadButton
					size="small"
					onChange={unpromisify(handleFileChange)}
					accept={ACCEPTED_FILE}
					disabled={isVerifying}
					loading={isVerifying}
					label={observableFileContentController ? t("SELECT_NEW_FILE") : t("BUTTON.SELECT")}
					qualifier="userSetting/Dropzone/upload"
				/>
			)}
		</DropzoneArea>
	);
};

const CustomerBox = (
	props: {
		customerData?: CustomerModelDTO;
		onSubmitAsync(): Promise<void>;
	} & MinimumDialogProps,
) => {
	const { show, onAnimationStateChange, onClose, ...rest } = props;
	const { customerData } = rest;
	const mode = customerData ? "edit" : "new";

	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const [currentTab, setCurrentTab] = useState<number>(0);
	const [syncTestStatus, setSyncTestStatus] = useState<
		Record<"import" | "export", "success" | "failed" | "default" | "thinking">
	>({ import: "default", export: "default" });

	const protocolsApi = useApiGen(ProtocolsControllerApiFactory);
	const integrationsApi = useApiGen(IntegrationsControllerV3ApiFactory);

	const apiKeyExistence = useQuery({
		...userRepository.customerIntegrationApiKey.getQueryConfig(customerData?.id ?? ""),
		enabled: customerData?.id != null,
		// A caller who is not ROOT is refused, and asking again cannot change that.
		retry: false,
	});
	const apiKeyStatus: IntegrationApiKeyStatus =
		customerData?.id == null
			? "absent"
			: apiKeyExistence.isError
				? "unknown"
				: apiKeyExistence.data == null
					? "checking"
					: apiKeyExistence.data.exists
						? "stored"
						: "absent";

	const importServices = userRepository.retrieveServicesType.useQuery();
	const exportServices = useQueryNoRefetch(["retrieveExportConverterType"], {
		queryFn: () => axiosExtract(integrationsApi.retrieveExportConverterType()),
	});

	// CustomerModelDTO payload for updating customer
	const TABS = ["Info", "Services"];

	const customerSyncData = useMemo(
		() => ({
			...defaultNewCustomerData,
			...customerData,
			// A ceiling stored before it dropped to 13 already applies as 13 (updateUser); opening on it keeps the form saveable.
			...(customerData?.decimalPreference != null && {
				decimalPreference: clamp(
					customerData.decimalPreference,
					CUSTOMER_DECIMAL_PLACES_MIN,
					CUSTOMER_DECIMAL_PLACES_MAX,
				),
			}),
			// Always seeded, and never from the row's key fields: the key field starts EMPTY, and whether a key is
			// stored comes from the existence endpoint (`apiKeyStatus`, kept in sync below).
			integration: {
				enabled: customerData?.integration?.enabled ?? false,
				events: customerData?.integration?.events ?? [],
				apiKey: "",
				apiKeyStatus: (customerData?.id == null ? "absent" : "checking") as IntegrationApiKeyStatus,
				removeApiKey: false,
			},
			// Always seeded, like `integration`: every customer has these settings
			// whether or not a key has been filled in, and the group has to exist for the
			// switch below to be controlled. `apiKey` is deliberately left out of the
			// defaults — the field starts EMPTY, and an empty-and-untouched field means
			// "leave the stored key alone" (see onSubmitAsync).
			mcpBotSettings: {
				...(defaultNewCustomerData?.mcpBotSettings ?? {}),
				...(customerData?.mcpBotSettings ?? {}),
				apiKey: "",
				hasApiKey: customerData?.mcpBotSettings?.hasApiKey || false,
				webSearchEnabled: customerData?.mcpBotSettings?.webSearchEnabled || false,
				removeApiKey: false,
			},
			// Seeded for the switch, like `mcpBotSettings`. A stored secret arrives as a placeholder, so both
			// secret fields start EMPTY and an untouched one keeps what is stored (see smartXSettingsToSave).
			smartXSettings: {
				enabled: customerData?.smartXSettings?.enabled ?? false,
				enterpriseId: customerData?.smartXSettings?.enterpriseId ?? "",
				clientId: customerData?.smartXSettings?.clientId ?? "",
				username: customerData?.smartXSettings?.username ?? "",
				clientSecret: "",
				password: "",
				hasClientSecret: customerData?.smartXSettings?.hasClientSecret ?? false,
				hasPassword: customerData?.smartXSettings?.hasPassword ?? false,
				removeClientSecret: false,
				removePassword: false,
			},
			snapshotPolicy: snapshotPolicyFormValues(customerData?.snapshotPolicy),
		}),
		[customerData],
	);

	const { control, formState, handleSubmit, setValue, setError, trigger, watch, getValues, getFieldState, resetField } =
		useForm({
			defaultValues: customerSyncData,
			resolver: zodResolver(makeCustomerSchema(t)),
			mode: "onTouched",
		});
	// Read in render so `formState` subscribes to it: after a refused save, the actions below re-validate what
	// they change.
	const { isSubmitted } = formState;

	useEffect(() => {
		setValue("integration.apiKeyStatus", apiKeyStatus, { shouldValidate: true });
	}, [apiKeyStatus, setValue]);

	const [integrationEnabled, removeIntegrationApiKey, integrationApiKeyStatus] = watch([
		"integration.enabled",
		"integration.removeApiKey",
		"integration.apiKeyStatus",
	]);

	const setIntegrationApiKeyRemoval = (removing: boolean) => {
		resetField("integration.apiKey");
		setValue("integration.removeApiKey", removing, { shouldDirty: true });
		if (isSubmitted) {
			void trigger("integration.apiKey");
		}
	};
	const setMcpBotApiKeyRemoval = (removing: boolean) => {
		resetField("mcpBotSettings.apiKey");
		setValue("mcpBotSettings.removeApiKey", removing, { shouldDirty: true });
	};

	// The resolver files a list-level issue under `root` while hook rows are mounted, and on the list itself
	// when there are none.
	const integrationEventsError =
		formState.errors.integration?.events?.root?.message ?? formState.errors.integration?.events?.message;

	/**
	 * The Sphere AI group as it should go on the wire.
	 *
	 * The Anthropic key is write-only: the field starts empty and is submitted ONLY
	 * when it was actually typed into, so saving the dialog for any other reason —
	 * flipping web search, renaming the customer — cannot wipe the stored key with
	 * the blank it started as. Removing it without typing a new one sends `""`. `hasApiKey` is sent as the truth
	 * after this save (one is on file, or one is being set now).
	 */
	function mcpBotSettingsToSave(
		values: typeof customerSyncData,
	): NonNullable<CustomerModelDTO["mcpBotSettings"]> | undefined {
		const group = values.mcpBotSettings;
		if (!group) return undefined;
		const typedANewKey = getFieldState("mcpBotSettings.apiKey").isDirty && Boolean(group.apiKey);
		if (group.removeApiKey && !typedANewKey) {
			return { webSearchEnabled: group.webSearchEnabled ?? false, hasApiKey: false, apiKey: "" };
		}
		return {
			webSearchEnabled: group.webSearchEnabled ?? false,
			hasApiKey: typedANewKey || Boolean(group.hasApiKey),
			apiKey: typedANewKey ? (group.apiKey ?? undefined) : undefined,
		};
	}

	/**
	 * The SmartX group as it should go on the wire. The backend stores the group it receives whole, so a
	 * configured customer always sends it; one never configured keeps `null` until something is set. A
	 * secret goes out only when a new one was typed, and as typed, or blank when it is removed without one: the backend
	 * keeps a secret it does not receive and deletes one it receives blank. The ids and the username go out trimmed, as validation reads
	 * them. `hasClientSecret` / `hasPassword` are read-only and are not sent.
	 */
	function smartXSettingsToSave(values: typeof customerSyncData): CustomerModelDTO["smartXSettings"] {
		const group = values.smartXSettings;
		const anythingSet =
			group.enabled ||
			[group.enterpriseId, group.clientId, group.username, group.clientSecret, group.password].some((value) =>
				value?.trim(),
			);
		if (!customerData?.smartXSettings && !anythingSet) return undefined;
		const removing = { clientSecret: group.removeClientSecret, password: group.removePassword } satisfies Record<
			SmartXSecret,
			boolean | undefined
		>;
		const typedNew = (secret: SmartXSecret) => {
			if (getFieldState(`smartXSettings.${secret}`).isDirty && group[secret]?.trim()) {
				return group[secret];
			}
			return removing[secret] ? "" : undefined;
		};
		return {
			enabled: group.enabled ?? false,
			enterpriseId: group.enterpriseId?.trim() || undefined,
			clientId: group.clientId?.trim() || undefined,
			username: group.username?.trim() || undefined,
			clientSecret: typedNew("clientSecret"),
			password: typedNew("password"),
		};
	}

	/**
	 * The webhook integration as it should go on the wire: always, with `enabled` and its events, so a save made
	 * for any other reason leaves it as it is. The key is write-only: a typed key is sent, a removal with nothing
	 * typed sends `""`, and otherwise `apiKey` is left out and the backend keeps the stored one.
	 */
	function integrationToSave(values: typeof customerSyncData): NonNullable<CustomerModelDTO["integration"]> {
		const group = values.integration;
		return {
			enabled: group.enabled ?? false,
			events: group.events ?? [],
			apiKey: group.apiKey?.trim() ? group.apiKey : group.removeApiKey ? "" : undefined,
		};
	}

	function onSubmitAsync() {
		return new Promise((resolve, reject) => {
			handleSubmit(
				async (values) => {
					setIsLoading(true);
					try {
						switch (mode) {
							case "new": {
								await userRepository.createCustomer({
									...values,
									integration: integrationToSave(values),
									mcpBotSettings: mcpBotSettingsToSave(values),
									smartXSettings: smartXSettingsToSave(values),
									snapshotPolicy: snapshotPolicyToSave(customerData?.snapshotPolicy, values.snapshotPolicy),
								});
								break;
							}
							case "edit": {
								await userRepository.updateCustomer({
									...values,
									integration: integrationToSave(values),
									mcpBotSettings: mcpBotSettingsToSave(values),
									smartXSettings: smartXSettingsToSave(values),
									snapshotPolicy: snapshotPolicyToSave(customerData?.snapshotPolicy, values.snapshotPolicy),
								});
								break;
							}
						}
						await props.onSubmitAsync();
					} catch (e) {
						const apiKeyRejection = await integrationApiKeyRejection(e);
						if (apiKeyRejection) {
							setError("integration.apiKey", { type: "server", message: apiKeyRejection });
							setCurrentTab(1);
						} else {
							platformToast({
								children: t("SETTING_PANEL.CREATE_CUSTOMER_ERROR"),
								severity: "error",
								icon: "Top-menu-user",
							});
						}
						reject(e);
					} finally {
						setIsLoading(false);
					}
				},
				(e) => {
					console.log(e);
					reject(e);
				},
			)().then(resolve, reject);
		});
	}

	const onTestSyncProtocol = React.useCallback(
		async (target: "import" | "export", connectionParams: ConnectionProperties) => {
			try {
				setSyncTestStatus((prev) => ({ ...prev, [target]: "thinking" }));
				await protocolsApi.ftpCheck(connectionParams);
				setSyncTestStatus((prev) => ({ ...prev, [target]: "success" }));
			} catch (error) {
				setSyncTestStatus((prev) => ({ ...prev, [target]: "failed" }));
				console.log(error);
			}
		},
		[protocolsApi],
	);

	const smartXFieldLabels: Record<SmartXRequiredField, string> = {
		enterpriseId: "Enterprise ID",
		clientId: "Client ID",
		username: "Username",
		clientSecret: "Client secret",
		password: t("SETTING_PANEL.PASSWORD"),
	};

	// TODO(MDOT-25446): Save is disabled while invalid, so field errors never show here or in spawn-user-dialog,
	// and this list stands in for them. The rest of the app keeps Save enabled and shows errors under each
	// field on submit: move both dialogs to that and drop the list?
	const missingSmartX = missingSmartXSettings(watch("smartXSettings"));

	const smartXHeader = (headerProps: CollapsibleHeaderProps) => (
		<DefaultCollapsibleHeader {...headerProps}>
			<div className="flex gap-4 items-center">
				<p>SmartX</p>
				<FormFields.Switch
					onClick={(e) => e.stopPropagation()}
					control={control}
					formState={formState}
					name="smartXSettings.enabled"
				/>
			</div>
		</DefaultCollapsibleHeader>
	);

	const snapshotPolicyHeader = (headerProps: CollapsibleHeaderProps) => (
		<DefaultCollapsibleHeader {...headerProps}>
			<div className="flex gap-4 items-center">
				<p>Snapshot policy</p>
				<FormFields.Switch
					onClick={(e) => e.stopPropagation()}
					control={control}
					formState={formState}
					name="snapshotPolicy.enabled"
				/>
			</div>
		</DefaultCollapsibleHeader>
	);

	function removeSnapshotRule(index: number) {
		setValue(
			"snapshotPolicy.rules",
			getValues().snapshotPolicy.rules.filter((_, i) => i !== index),
			{ shouldValidate: true },
		);
	}

	function addSnapshotRule() {
		setValue(
			"snapshotPolicy.rules",
			[...getValues().snapshotPolicy.rules, { type: SnapshotRuleTypeEnum.LastDaysOfMonth }],
			{ shouldValidate: true },
		);
	}

	/** A SmartX secret, with what is on file seen and acted on the same way as the other secrets. */
	function smartXSecretField(secret: SmartXSecret) {
		const [hasField, removeField] =
			secret === "clientSecret"
				? (["smartXSettings.hasClientSecret", "smartXSettings.removeClientSecret"] as const)
				: (["smartXSettings.hasPassword", "smartXSettings.removePassword"] as const);
		const setRemoving = (removing: boolean) => {
			resetField(`smartXSettings.${secret}`);
			setValue(removeField, removing, { shouldDirty: true });
			if (isSubmitted) {
				void trigger(`smartXSettings.${secret}`);
			}
		};
		return (
			<SecretField
				label={smartXFieldLabels[secret]}
				status={watch(hasField) ? "set" : "unset"}
				removing={Boolean(watch(removeField))}
				error={formState.errors.smartXSettings?.[secret]?.message}
				onRemove={() => setRemoving(true)}
				onUndoRemove={() => setRemoving(false)}
				onCancelReplace={() => resetField(`smartXSettings.${secret}`)}
				qualifier={`customerDialog/smartX/${secret}`}
			>
				<FormFields.Password
					control={control}
					formState={formState}
					name={`smartXSettings.${secret}`}
					label={smartXFieldLabels[secret]}
					placeholder={smartXFieldLabels[secret]}
					autoComplete="off"
				/>
			</SecretField>
		);
	}

	return (
		<Dialog
			size="xxlarge"
			noValidate
			show={show}
			onClose={onClose}
			onAnimationStateChange={onAnimationStateChange}
			header={customerData ? `Customer update ${customerData?.name ?? customerData?.email ?? ""}` : "Create Customer"}
			onSubmitAsync={onSubmitAsync}
			footer={({ state }) => (
				<DialogFooter
					primaryAction={
						<SubmitButton
							palette="primary"
							data-qualifier="customerDialog/submit"
							// Held while the stored-key check runs: until it answers, the schema reports a missing key.
							disabled={integrationEnabled && integrationApiKeyStatus === "checking"}
						>
							{customerData ? t("SETTING_PANEL.UPDATE_CUSTOMER") : t("SETTING_PANEL.CREATE_CUSTOMER")}
						</SubmitButton>
					}
					neutralAction={
						<Button palette="tertiary" disabled={state === "loading"} onClick={onClose}>
							{t("CANCEL")}
						</Button>
					}
				/>
			)}
		>
			<TabGroup tabIndex={currentTab} onTabChange={setCurrentTab}>
				<Tab
					className="rounded-sm bg-white overflow-hidden"
					key={0}
					title={(titleProps) => (
						<DefaultTabTitle
							{...titleProps}
							style={{
								color:
									formState.errors.name ||
									formState.errors.theme ||
									formState.errors.maxUsers ||
									formState.errors.maxPortfolios ||
									formState.errors.decimalPreference ||
									formState.errors.enabled
										? themeCSSVars.palette_D500
										: undefined,
							}}
						>
							{TABS[0]}
						</DefaultTabTitle>
					)}
				>
					{!customerSyncData ? (
						<IconWalls.ErrorData />
					) : (
						<WallOverlay showOverlay={isLoading} overlay={<IconWalls.LoadingData opaque={false} />}>
							<div className="grid gap-2">
								<div className="grid grid-cols-2 gap-4">
									<FormFields.Text
										control={control}
										formState={formState}
										name="name"
										label={t("SETTING_PANEL.NAME")}
										placeholder={t("SETTING_PANEL.NAME")}
									/>
									<FormFields.Text
										control={control}
										formState={formState}
										name="theme"
										label={t("SETTING_PANEL.THEME")}
										placeholder={t("SETTING_PANEL.THEME")}
									/>
								</div>
								<div className="grid grid-cols-2 gap-4">
									<FormFields.NullableNumber
										control={control}
										formState={formState}
										name="maxUsers"
										label={t("SETTING_PANEL.MAX_USERS")}
										placeholder={t("SETTING_PANEL.MAX_USERS")}
									/>
									<FormFields.NullableNumber
										control={control}
										formState={formState}
										name="maxPortfolios"
										label={t("SETTING_PANEL.MAX_PORTFOLIOS")}
										placeholder={t("SETTING_PANEL.MAX_PORTFOLIOS")}
									/>
								</div>
								<div className="grid grid-cols-2 gap-4">
									<FormFields.NullableNumber
										control={control}
										formState={formState}
										name="decimalPreference"
										label={t("SETTING_PANEL.DECIMAL_PLACES")}
										placeholder={`${CUSTOMER_DECIMAL_PLACES_MIN}-${CUSTOMER_DECIMAL_PLACES_MAX}`}
										// No min/max: NumberInput clamps to them on blur, which would replace an
										// out-of-range entry instead of letting the schema reject it.
										step={1}
									/>
									<FormFields.Select
										control={control}
										formState={formState}
										name="bondDataAccessTier"
										label="Bond data access tier"
										options={bondDataAccessTierOptions}
									/>
								</div>
								<div className="grid grid-cols-2 gap-4">
									<FormFields.Select
										control={control}
										formState={formState}
										name="fundDataAccessTier"
										label="Fund data access tier"
										options={fundDataAccessTierOptions}
									/>
								</div>
								<div className="flex flex-col">
									<p className="text-[12px] leading-[16px] text-[#616161] font-semibold">Primary logo</p>
									{watch("primaryLogo") ? (
										<div
											className={toClassName({
												[`relative rounded-sm flex flex-1 gap-4 p-2 justify-between items-center border-2 `]: true,
												[`bg-[color:var(--palette-N0)] border-[color:var(--palette-P600)]`]: true,
											})}
										>
											<div className="flex items-center gap-2">
												<SuccessImportBox />
												<p className="font-semibold">File Uploaded</p>
											</div>
											<Button
												palette="secondary"
												onClick={() => setValue("primaryLogo", undefined)}
												data-qualifier="userSetting/primaryLogo/delete"
											>
												<Icon icon="Delete" />
											</Button>
										</div>
									) : (
										<LoadCustomImage onChange={(url) => setValue("primaryLogo", url)} />
									)}
								</div>
								<div className="flex flex-col">
									<p className="text-[12px] leading-[16px] text-[#616161] font-semibold">Submark logo</p>
									{watch("submarkLogo") ? (
										<div
											className={toClassName({
												[`relative rounded-sm flex flex-1 gap-4 p-2 justify-between items-center border-2 `]: true,
												[`bg-[color:var(--palette-N0)] border-[color:var(--palette-P600)]`]: true,
											})}
										>
											<div className="flex items-center gap-2">
												<SuccessImportBox />
												<p className="font-semibold">File Uploaded</p>
											</div>
											<Button
												palette="secondary"
												onClick={() => setValue("submarkLogo", undefined)}
												data-qualifier="userSetting/submarkLogo/delete"
											>
												<Icon icon="Delete" />
											</Button>
										</div>
									) : (
										<LoadCustomImage onChange={(url) => setValue("submarkLogo", url)} />
									)}
								</div>

								<FormFields.Checkbox
									control={control}
									formState={formState}
									name="enabled"
									label={t("SETTING_PANEL.CUSTOMER_ENABLED")}
								>
									{t("SETTING_PANEL.CUSTOMER_ENABLED")}
								</FormFields.Checkbox>
							</div>
						</WallOverlay>
					)}
				</Tab>
				<Tab
					className="rounded-sm bg-white overflow-hidden"
					key={1}
					title={(titleProps) => (
						<DefaultTabTitle
							{...titleProps}
							style={{
								color:
									!(
										formState.errors.name ||
										formState.errors.theme ||
										formState.errors.maxUsers ||
										formState.errors.maxPortfolios ||
										formState.errors.decimalPreference ||
										formState.errors.enabled
									) && Object.values(formState.errors).some(Boolean)
										? themeCSSVars.palette_D500
										: undefined,
							}}
						>
							{TABS[1]}
						</DefaultTabTitle>
					)}
				>
					<div className="grid gap-4">
						<Collapsible
							header={(headerPros) => (
								<DefaultCollapsibleHeader {...headerPros}>
									<div className="flex gap-4 items-center">
										<p>Import Sync</p>
										<FormFields.Switch
											onClick={(e) => e.stopPropagation()}
											control={control}
											formState={formState}
											name="importData.enabled"
										/>
									</div>
								</DefaultCollapsibleHeader>
							)}
						>
							{({ expand }) => (
								<DefaultCollapsibleContent expand={expand}>
									<div className="grid grid-cols-4 gap-4">
										<FormFields.Text
											control={control}
											formState={formState}
											name="importData.username"
											label={t("SETTING_PANEL.NAME")}
											placeholder={t("SETTING_PANEL.NAME")}
										/>
										{!watch("importData.password") || formState.dirtyFields.importData?.password ? (
											<FormFields.Password
												control={control}
												formState={formState}
												name="importData.password"
												label={t("SETTING_PANEL.PASSWORD")}
												placeholder={t("SETTING_PANEL.PASSWORD")}
												autoComplete="off"
											/>
										) : (
											<div>
												<Label htmlFor="dummy">Password</Label>
												<Button
													palette="neutralOutline"
													className="w-full"
													size="small"
													onClick={() => setValue("importData.password", "")}
												>
													<div className="flex justify-between items-center w-full">
														<p>Change Password</p>
														<Icon icon="Top-menu-password" size={16} />
													</div>
												</Button>
											</div>
										)}

										<FormFields.Text
											control={control}
											formState={formState}
											name="importData.url"
											label={t("SETTING_PANEL.URL")}
											placeholder={t("SETTING_PANEL.URL")}
										/>

										<FormFields.NullableNumber
											control={control}
											formState={formState}
											name="importData.pollingFrequencyInMinutes"
											label={t("SETTING_PANEL.FREQUENCY")}
											placeholder={t("SETTING_PANEL.FREQUENCY")}
										/>
										<div className="col-span-2">
											<FormFields.Select
												control={control}
												formState={formState}
												name="importData.importServiceType"
												label={t("SETTING_PANEL.CONVERTER_SERVICES")}
												options={
													importServices.data?.map((service) => ({
														label: t(`IMPORT.${service}`, { defaultValue: service }),
														value: service,
													})) ?? []
												}
											/>
										</div>
										<div className="col-span-2">
											<FormFields.NullableNumber
												control={control}
												formState={formState}
												name="importData.port"
												label={t("SETTING_PANEL.PORT")}
												placeholder={t("SETTING_PANEL.PORT")}
											/>{" "}
										</div>

										<div className="col-span-4">
											<Button
												palette="primary"
												size="small"
												className="w-full justify-center"
												data-qualifier="customerDialog/sync(import)/testConnection"
												onClickAsync={() =>
													onTestSyncProtocol("import", {
														connectionUrl: getValues("importData.url"),
														password: getValues("importData.password"),
														port: getValues("importData.port"),
														username: getValues("importData.username"),
													})
												}
											>
												Test connection
											</Button>
											<p
												className={toClassName({
													"text-center font-semibold": true,
													"text-[color:var(--palette-D400)] animate-shake": syncTestStatus.import === "failed",
												})}
											>
												<Switch
													case={syncTestStatus.import}
													match={{
														thinking: () => "...",
														default: () => "",
														failed: () => "Your connect failed, please update your settings",
														success: () => "You successfully establish a connection",
													}}
												/>
											</p>
										</div>
									</div>
								</DefaultCollapsibleContent>
							)}
						</Collapsible>
						<Collapsible
							header={(headerProps) => (
								<DefaultCollapsibleHeader {...headerProps}>
									<div className="flex gap-4 items-center">
										<p>Export Sync</p>
										<FormFields.Switch
											onClick={(e) => e.stopPropagation()}
											control={control}
											formState={formState}
											name="exportData.enabled"
											disabled={isExecutionWebhookActive(watch("integration"))}
										/>
									</div>
								</DefaultCollapsibleHeader>
							)}
						>
							{({ expand }) => (
								<DefaultCollapsibleContent expand={expand}>
									<div className="grid grid-cols-4 gap-4">
										<FormFields.Text
											control={control}
											formState={formState}
											name="exportData.username"
											label={t("SETTING_PANEL.NAME")}
											placeholder={t("SETTING_PANEL.NAME")}
										/>
										{!watch("exportData.password") || formState.dirtyFields.exportData?.password ? (
											<FormFields.Password
												control={control}
												formState={formState}
												name="exportData.password"
												label={t("SETTING_PANEL.PASSWORD")}
												placeholder={t("SETTING_PANEL.PASSWORD")}
												autoComplete="off"
											/>
										) : (
											<div>
												<Label htmlFor="dummy">Password</Label>
												<Button
													palette="neutralOutline"
													className="w-full"
													size="small"
													onClick={() => setValue("exportData.password", "")}
												>
													<div className="flex justify-between items-center w-full">
														<p>Change Password</p>
														<Icon icon="Top-menu-password" size={16} />
													</div>
												</Button>
											</div>
										)}

										<FormFields.Text
											control={control}
											formState={formState}
											name="exportData.url"
											label={t("SETTING_PANEL.URL")}
											placeholder={t("SETTING_PANEL.URL")}
										/>

										<FormFields.NullableNumber
											control={control}
											formState={formState}
											name="exportData.port"
											label={t("SETTING_PANEL.PORT")}
											placeholder={t("SETTING_PANEL.PORT")}
										/>
										<div className="col-span-2">
											<FormFields.Select
												control={control}
												formState={formState}
												name="exportData.exportServiceType"
												label={t("SETTING_PANEL.CONVERTER_SERVICES")}
												options={
													exportServices.data?.map((service) => ({ label: t(`EXPORT.${service}`), value: service })) ??
													[]
												}
											/>
										</div>
										<div className="col-span-2">
											<FormFields.Select
												control={control}
												formState={formState}
												name="exportData.exportConnectionType"
												label="Connection type"
												options={exportConnectionTypeOptions}
											/>
										</div>
										<div className="col-span-4">
											<FormFields.Text
												control={control}
												formState={formState}
												name="exportData.directory"
												label={t("SETTING_PANEL.DIRECTORY")}
												placeholder={t("SETTING_PANEL.DIRECTORY")}
											/>
										</div>

										<div className="col-span-4">
											<Button
												palette="primary"
												size="small"
												className="w-full justify-center"
												data-qualifier="customerDialog/sync(export)/testConnection"
												onClickAsync={() =>
													onTestSyncProtocol("export", {
														connectionUrl: getValues("exportData.url"),
														password: getValues("exportData.password"),
														port: getValues("exportData.port"),
														username: getValues("exportData.username"),
													})
												}
											>
												Test connection
											</Button>
											<p
												className={toClassName({
													"text-center font-semibold": true,
													"text-[color:var(--palette-D400)] animate-shake": syncTestStatus.export === "failed",
												})}
											>
												<Switch
													case={syncTestStatus.export}
													match={{
														thinking: () => "...",
														default: () => "",
														failed: () => "Your connect failed, please update your settings",
														success: () => "You successfully establish a connection",
													}}
												/>
											</p>
										</div>
									</div>
								</DefaultCollapsibleContent>
							)}
						</Collapsible>
						<Collapsible
							header={(headerProps) => (
								<DefaultCollapsibleHeader {...headerProps}>
									<div className="flex gap-4 items-center">
										<p>Custom market views with commentaries</p>
										<FormFields.Switch
											onClick={(e) => e.stopPropagation()}
											control={control}
											formState={formState}
											name="marketViewSettings.commentaryEditorVisible"
										/>
									</div>
								</DefaultCollapsibleHeader>
							)}
						>
							{() => <></>}
						</Collapsible>
						<Collapsible
							header={(headerProps) => (
								<DefaultCollapsibleHeader {...headerProps}>
									<div className="flex gap-4 items-center">
										<p>Customize portfolio pdf</p>
										{customerData?.id && (
											<span onClick={(e) => e.stopPropagation()}>
												<Button
													palette="secondary"
													size="x-small"
													onClick={() =>
														window.open(typedUrlForRoute("Report/Editor", { customerId: customerData.id! }), "_blank")
													}
												>
													Pro editor
												</Button>
											</span>
										)}
									</div>
								</DefaultCollapsibleHeader>
							)}
						>
							{({ expand }) => (
								<DefaultCollapsibleContent expand={expand}>
									<div className="grid gap-3 relative">
										<Text as="p" type="Body/L/Bold" className=" border-b border-[#616161] border-solid  drop-shadow ">
											Cover
										</Text>
										<div className="grid grid-cols-2 gap-4">
											<div className="flex flex-col">
												<p className="text-[12px] leading-[16px] text-[#616161] font-semibold">logo</p>
												{watch("clientLogo") ? (
													<div
														className={toClassName({
															[`relative rounded-sm flex flex-1 gap-4 p-2 justify-between items-center border-2 `]: true,
															[`bg-[color:var(--palette-N0)] border-[color:var(--palette-P600)]`]: true,
														})}
													>
														<div className="flex items-center gap-2">
															<SuccessImportBox />
															<p className="font-semibold">File Uploaded</p>
														</div>
														<Button palette="secondary" onClick={() => setValue("clientLogo", undefined)}>
															<Icon icon="Delete" />
														</Button>
													</div>
												) : (
													<LoadCustomImage onChange={(url) => setValue("clientLogo", url)} />
												)}
											</div>
											<div className="flex flex-col">
												<p className="text-[12px] leading-[16px] text-[#616161] font-semibold">background</p>
												{watch("backgroundImage") ? (
													<div
														className={toClassName({
															[`relative rounded-sm flex flex-1 gap-4 p-2 justify-between items-center border-2 `]: true,
															[`bg-[color:var(--palette-N0)] border-[color:var(--palette-P600)]`]: true,
														})}
													>
														<div className="flex items-center gap-2">
															<SuccessImportBox />
															<p className="font-semibold">File Uploaded</p>
														</div>
														<Button palette="secondary" onClick={() => setValue("backgroundImage", undefined)}>
															<Icon icon="Delete" />
														</Button>
													</div>
												) : (
													<LoadCustomImage onChange={(url) => setValue("backgroundImage", url)} />
												)}
											</div>
										</div>

										<Text as="p" type="Body/L/Bold" className="border-b border-[#616161] border-solid  drop-shadow ">
											Back cover
										</Text>
										<div className="grid grid-cols-2 gap-2">
											<FormFields.TextArea
												control={control}
												formState={formState}
												label="disclaimer"
												name="disclaimer"
												rows={4}
											/>
											<FormFields.TextArea
												control={control}
												formState={formState}
												label="address"
												name="address"
												rows={4}
											/>
											<FormFields.TextArea
												control={control}
												formState={formState}
												label="email"
												name="email"
												rows={4}
											/>
											<FormFields.TextArea
												control={control}
												formState={formState}
												label="website"
												name="website"
												rows={4}
											/>
										</div>
									</div>
								</DefaultCollapsibleContent>
							)}
						</Collapsible>

						<Collapsible header="Webhooks">
							{({ expand }) => (
								<DefaultCollapsibleContent expand={expand}>
									<Column flexGrow={0} gap={8}>
										<FormFields.Checkbox
											control={control}
											formState={formState}
											name="integration.enabled"
											rules={{ deps: ["integration.events", "integration.apiKey"] }}
										>
											Enable integration
										</FormFields.Checkbox>
										<SecretField
											label="API Key"
											status={integrationApiKeySecretStatus[integrationApiKeyStatus ?? "checking"]}
											removing={Boolean(removeIntegrationApiKey)}
											error={formState.errors.integration?.apiKey?.message}
											onRemove={() => setIntegrationApiKeyRemoval(true)}
											onUndoRemove={() => setIntegrationApiKeyRemoval(false)}
											onCancelReplace={() => resetField("integration.apiKey")}
											onRetry={() => void apiKeyExistence.refetch()}
											qualifier="customerDialog/integration/apiKey"
										>
											<FormFields.Password
												control={control}
												formState={formState}
												name="integration.apiKey"
												label="API Key"
												placeholder="Paste the API key"
												autoComplete="off"
											/>
										</SecretField>
										<ForEach collection={watch("integration.events") ?? []}>
											{({ event }, index) => (
												<Row flexGrow={0} gap={8} alignItems="start">
													<FormFields.Select
														control={control}
														formState={formState}
														name={`integration.events.${index}.event`}
														label="Action"
														className="w-48"
														options={integrationEventKeys.map((k) => ({
															label: integrationEventLabels[k],
															value: k,
															disabled:
																(event !== k && watch("integration.events")?.some((ev) => ev.event === k)) ||
																(k === IntegrationEventEventEnum.Execute && Boolean(watch("exportData.enabled"))),
														}))}
													/>
													<FormFields.Text
														type="url"
														control={control}
														formState={formState}
														label="Target URL"
														placeholder="https://api.example.com/integrations/mdotm?abc=1&something=demo"
														name={`integration.events.${index}.url`}
														className="grow"
													/>
													<FormField label={<>&nbsp;</>} className="shrink">
														{(forward) => (
															<IconButton
																{...forward}
																size={18}
																color={themeCSSVars.palette_P600}
																icon="Delete"
																onClick={() => {
																	const next = getValues().integration?.events?.slice() ?? [];
																	next.splice(index, 1);
																	setValue(`integration.events`, next, { shouldValidate: isSubmitted });
																}}
															/>
														)}
													</FormField>
												</Row>
											)}
										</ForEach>
										{integrationEventKeys.some(
											(key) =>
												!watch("integration.events")?.some((x) => x.event === key) &&
												!(key === IntegrationEventEventEnum.Execute && watch("exportData.enabled")),
										) && (
											<Row flexGrow={0} gap={2}>
												<Button
													size="x-small"
													palette="secondary"
													onClick={() => {
														const newIndex = getValues().integration?.events?.length ?? 0;
														flushSync(() =>
															setValue(
																`integration.events`,
																[
																	...(getValues().integration?.events ?? []),
																	{
																		event: integrationEventKeys.find(
																			(key) =>
																				!getValues().integration?.events?.some((x) => x.event === key) &&
																				!(key === IntegrationEventEventEnum.Execute && getValues().exportData?.enabled),
																		)!,
																		url: "",
																	},
																],
																{ shouldValidate: isSubmitted },
															),
														);
														try {
															document
																.querySelector<HTMLInputElement>(`[name="integration.events.${newIndex}.url"]`)
																?.focus({ preventScroll: true });
														} catch {
															// ignored
														}
													}}
												>
													<Icon icon="Outline1" color={themeCSSVars.palette_P600} />
													&nbsp; Add hook
												</Button>
											</Row>
										)}

										{integrationEventsError && (
											<Banner severity="error" data-qualifier="customerDialog/integration/eventsError">
												{integrationEventsError}
											</Banner>
										)}
									</Column>
								</DefaultCollapsibleContent>
							)}
						</Collapsible>

						<Collapsible header="Sphere AI">
							{({ expand }) => (
								<DefaultCollapsibleContent expand={expand}>
									<Column flexGrow={0} gap={8}>
										<Banner severity="info">
											Sphere AI runs on this customer&apos;s OWN Anthropic account: every chat turn is billed there, and
											usage limits are set there too. Without a key the assistant cannot answer at all — users are told
											to ask an administrator for one.{" "}
											<ActionText
												href="https://platform.claude.com/settings/keys"
												target="_blank"
												rel="noreferrer noopener"
											>
												Create or manage keys on platform.claude.com
											</ActionText>
											.
										</Banner>
										<SecretField
											label="Anthropic API key"
											status={watch("mcpBotSettings.hasApiKey") ? "set" : "unset"}
											removing={Boolean(watch("mcpBotSettings.removeApiKey"))}
											onRemove={() => setMcpBotApiKeyRemoval(true)}
											onUndoRemove={() => setMcpBotApiKeyRemoval(false)}
											onCancelReplace={() => resetField("mcpBotSettings.apiKey")}
											qualifier="customerDialog/mcpBot/apiKey"
										>
											<FormFields.Password
												control={control}
												formState={formState}
												name="mcpBotSettings.apiKey"
												label="Anthropic API key"
												placeholder="sk-ant-…"
												autoComplete="off"
											/>
										</SecretField>
										<FormFields.Checkbox control={control} formState={formState} name="mcpBotSettings.webSearchEnabled">
											Allow the assistant to read public web pages
										</FormFields.Checkbox>
									</Column>
								</DefaultCollapsibleContent>
							)}
						</Collapsible>

						<Collapsible header={smartXHeader}>
							{({ expand }) => (
								<DefaultCollapsibleContent expand={expand}>
									<Column flexGrow={0} gap={8}>
										<div className="grid grid-cols-3 gap-4">
											<FormFields.Text
												control={control}
												formState={formState}
												name="smartXSettings.enterpriseId"
												label={smartXFieldLabels.enterpriseId}
												placeholder={smartXFieldLabels.enterpriseId}
											/>
											<FormFields.Text
												control={control}
												formState={formState}
												name="smartXSettings.clientId"
												label={smartXFieldLabels.clientId}
												placeholder={smartXFieldLabels.clientId}
											/>
											<FormFields.Text
												control={control}
												formState={formState}
												name="smartXSettings.username"
												label={smartXFieldLabels.username}
												placeholder={smartXFieldLabels.username}
												autoComplete="off"
											/>
											{smartXSecretField("clientSecret")}
											{smartXSecretField("password")}
										</div>
										{missingSmartX.length > 0 && (
											<p className="text-[color:var(--palette-D400)]">
												Required while SmartX is on: {missingSmartX.map((field) => smartXFieldLabels[field]).join(", ")}
											</p>
										)}
									</Column>
								</DefaultCollapsibleContent>
							)}
						</Collapsible>

						<Collapsible header={snapshotPolicyHeader}>
							{({ expand }) => (
								<DefaultCollapsibleContent expand={expand}>
									<Column flexGrow={0} gap={8}>
										<FormFields.NullableNumber
											control={control}
											formState={formState}
											name="snapshotPolicy.retentionMonths"
											label="Retention (months)"
											placeholder="Retention (months)"
											// No min: NumberInput clamps to it on blur, which would replace an
											// out-of-range entry instead of letting the schema reject it.
											step={1}
										/>
										<ForEach collection={watch("snapshotPolicy.rules")}>
											{(_, index) => (
												<div className="grid grid-cols-[1fr_1fr_auto] gap-4">
													<FormFields.Select
														control={control}
														formState={formState}
														name={`snapshotPolicy.rules.${index}.type`}
														label="Rule"
														options={snapshotRuleTypeOptions}
													/>
													<FormFields.NullableNumber
														control={control}
														formState={formState}
														name={`snapshotPolicy.rules.${index}.days`}
														label="Days"
														placeholder={`${SNAPSHOT_RULE_DAYS_MIN}-${SNAPSHOT_RULE_DAYS_MAX}`}
														step={1}
													/>
													<FormField label={<>&nbsp;</>}>
														{(forward) => (
															<IconButton
																{...forward}
																size={18}
																color={themeCSSVars.palette_P600}
																icon="Delete"
																data-qualifier={`customerDialog/snapshotPolicy/rules/${index}/delete`}
																onClick={() => removeSnapshotRule(index)}
															/>
														)}
													</FormField>
												</div>
											)}
										</ForEach>
										<Row flexGrow={0} gap={2}>
											<Button size="x-small" palette="secondary" onClick={addSnapshotRule}>
												<Icon icon="Outline1" color={themeCSSVars.palette_P600} />
												&nbsp; Add rule
											</Button>
										</Row>
									</Column>
								</DefaultCollapsibleContent>
							)}
						</Collapsible>
					</div>
				</Tab>
			</TabGroup>
		</Dialog>
	);
};
